/**
 * @file App.js
 * @module AppMain
 * @description アプリの起動・状態管理
 */
import { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom'; // ✅ React Router v6 用に修正

import { GetConsoleToken, registerConsoleTokenListener } from './ConsoleTokenCtl';
import { PowerBIAuth } from './PowerBIAuth';
import PowerBICtl from './PowerBICtl';
import ScreenCreate from './ScreenCreate';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LicensePage from './LicensePage';
import './App.css'; 
import { SC_STATE , C_URLS , C_LOGWORDING , C_ERRWORDING } from './Constants';

/**
 * アプリケーションのメインコンポーネント。
 * アプリケーション画面の起動と状態管理を行う。
 *
 * @component
 * @returns {JSX.Element} アプリケーションのルート要素。
 */
const App = () => {
  const [loadingState, setLoadingState] = useState(SC_STATE.BI_TOKEN_FETCHING); // 画面の状態としてステート管理。初期状態をBIトークン取得中に設定
  const [bitoken, setBitoken] = useState(null);   // Power BI の埋め込みトークンを管理するステート管理。別でリフレッシュ更新時にレンダリングしたいため。
  const [consoleToken, setConsoleToken] = useState(null);   // 共通コンソールトークンを管理するステート。別でリフレッシュ更新時にレンダリングしたいため。
  const [error, setError] = useState(null);   // エラーを管理するステート。別でエラー発生時にレンダリングしたいため。
  const embedObjRef = useRef(null);           // 埋め込み用トークン・URL・有効期限を保持するためのref。レンダリングをトリガーしないため、useRefを使用
  const cnsltokenRef = useRef(consoleToken);  // 共通コンソールトークンを保持するためのref。レンダリングをトリガーしないため、useRefを使用

  // コールバックをローカルで定義
  const setEmbedData = (biTokenVal, embedUrl, expiration) => {
    embedObjRef.current = { bitoken: biTokenVal, embedURL: embedUrl, expiration };
    setBitoken(biTokenVal);
  };
  // コンソールトークン更新用のコールバックを定義
  const setConsoleTokenAndRef = (token) => {
    setConsoleToken(token);         // ステートも更新
    cnsltokenRef.current = token;   // refも最新値に更新
    //console.log('[App][setConsoleTokenAndRef]token=\n', token);
  };
  // コンソールトークンのイベントリスナーを登録
  registerConsoleTokenListener(setConsoleTokenAndRef);

  useEffect(() => {
    const init = async () => {
      try {
        // コンソールトークンの取得と検証
        cnsltokenRef.current = GetConsoleToken(setLoadingState);
        if (!cnsltokenRef.current) {
          setLoadingState(SC_STATE.ERR_NONPERMANENT);
          return;
        }
        // コンソールトークンをステートにセット
        setConsoleToken(cnsltokenRef.current);
        // 画面状態をBIトークン取得中に設定
        setLoadingState(SC_STATE.BI_TOKEN_FETCHING);
        // Power BI 認証を実行
        await PowerBIAuth(cnsltokenRef, setEmbedData, setLoadingState);
        // 認証成功後、状態を更新
        setLoadingState(SC_STATE.AUTHENTICATED);
      } catch (error) {
        // エラーが発生した場合はエラー状態を設定
        setError({
          errorCode: error.status || 'UNKNOWN',
          errorMessage: error.statusText || error.message || '不明なエラー',
        });
//        setLoadingState(SC_STATE.ERR_PERMANENT);
      }
    };

    init();
  }, []);
 //console.log('[App][useEffect][BeforeRTN]loadingState embedObjRef\n', loadingState,embedObjRef.current);

  return (
    <Router>
      <div className="App">
        <ToastContainer />
        <Routes>
          <Route  // ルートパスに対するルーティング
            path="/"
            element={
              loadingState === SC_STATE.AUTHENTICATED ? (
                <PowerBICtl
                  embedData={embedObjRef.current}
                  cnsltokenRef={cnsltokenRef}
                  setLoadingState={setLoadingState}
                  setEmbedData={setEmbedData}
                  setError={setError}
                />
              ) : (
                <ScreenCreate
                  message={loadingState}
                  error={error}
                />
              )
            }
          />
          <Route path="/licenseInfo" element={<LicensePage />} /> // ライセンス情報ページへのルーティング
        </Routes>
        <Link to="/licenseInfo" className="license-link">OSS License</Link>
      </div>
    </Router>
  );
};

export default App;
